import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Gift, Copy, X, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = 'aadc_referral_invite_cta_dismissed_v1';

/**
 * ReferralInviteCTA
 *
 * Sticky one-line dismissible banner on member dashboard promoting the referral
 * double-win: new signup gets 50% off, referrer gets a free month (up to 12).
 *
 * - Fetches user's referral code and shows a one-click share link
 * - Copy-to-clipboard button
 * - Session-scoped dismiss (returns after next session)
 */
export default function ReferralInviteCTA() {
  const [code, setCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(sessionStorage.getItem(DISMISS_KEY) === '1');
    let cancelled = false;
    axios
      .get(`${API}/api/referrals/my-code`)
      .then((res) => {
        if (!cancelled) setCode(res.data?.code || null);
      })
      .catch(() => {
        if (!cancelled) setCode(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading || dismissed || !code) return null;

  const shareUrl = `${window.location.origin}/register?ref=${code}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Invite link copied!');
    } catch {
      toast.error('Copy failed — select the link manually');
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div
      className="mb-6 rounded-lg border border-amber-200 bg-gradient-to-r from-amber-50 via-white to-amber-50 dark:from-amber-950/40 dark:via-background dark:to-amber-950/40 shadow-sm"
      data-testid="referral-invite-cta"
    >
      <div className="flex items-center gap-3 px-4 py-2.5 flex-wrap">
        <Gift className="w-5 h-5 text-amber-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm">
            <strong>Invite a friend</strong> — they get <strong>50% off</strong> their first year, you get a{' '}
            <strong>free month</strong>.
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 break-all">
            {shareUrl}
          </p>
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-amber-500 hover:bg-amber-600 text-white shrink-0 transition"
          data-testid="referral-cta-copy"
        >
          <Copy className="w-3.5 h-3.5" />
          Copy link
        </button>
        <Link
          to="/referrals"
          className="inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-800 shrink-0"
          data-testid="referral-cta-details"
        >
          Details
          <ChevronRight className="w-3.5 h-3.5" />
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          className="p-1 rounded hover:bg-amber-100 text-muted-foreground shrink-0"
          data-testid="referral-cta-dismiss"
          aria-label="Dismiss invite banner"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
